import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { CrossClientQueryEvaluationTypes, type CrossClientQueryEvaluation } from '../../../../models/CrossClient'
import { Button } from '../../../../components/Button/Button'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { pluralize } from '../../utils'
import { type CcqCard } from './ccqResultView'
import { useCcqAiGate } from './useCcqAiGate'
import { useCcqRun } from './useCcqRun'
import { useCrossClientEvaluationActions } from './useCrossClientEvaluationActions'

const AI_BODY = "I'll look through each client's meetings and notes and settle where they belong. I'll pause after this many clients."

const AI_CUSTOM_LABEL = 'Number of clients'

const AI_CUSTOM_PLACEHOLDER = 'Custom number'

const AI_CUSTOM_RUN = 'Run'

const OPTION_CLASS_NAME = 'button-small rounded-3 text-nowrap zocks-border-0.5 zocks-border-color-gray-300'

type QuickActionKey = 'ai-10' | 'ai-20' | 'ai-all' | 'ai-custom'

export interface CrossClientQuickActionPopoverProps {
  card: CcqCard
  onClose: () => void
  onStarted: (evaluation: CrossClientQueryEvaluation) => void
}

export const CrossClientQuickActionPopover: React.FC<CrossClientQuickActionPopoverProps> = ({ card, onClose, onStarted }) => {
  const { counts } = useCcqRun()
  const aiNext = counts[card]
  const { evaluateOutstanding } = useCrossClientEvaluationActions(card)
  const { isBlocked, tooltipTitle, tooltipContent } = useCcqAiGate()
  const [loadingKey, setLoadingKey] = useState<QuickActionKey | null>(null)
  const [customCount, setCustomCount] = useState('')

  const isLoading = loadingKey !== null
  const isDisabled = isLoading || isBlocked
  const customLimit = Number(customCount)
  const isCustomValid = customCount !== '' && Number.isInteger(customLimit) && customLimit >= 1 && customLimit <= aiNext

  const run = async (key: QuickActionKey, limit?: number) => {
    if (isLoading) {
      return
    }
    setLoadingKey(key)
    try {
      const evaluation = await evaluateOutstanding(CrossClientQueryEvaluationTypes.AI, limit)
      if (evaluation) {
        onStarted(evaluation)
      }
    } finally {
      setLoadingKey(null)
      onClose()
    }
  }

  const handleCustomSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (isCustomValid && !isDisabled) {
      void run('ai-custom', customLimit)
    }
  }

  const option = (key: QuickActionKey, label: string, limit?: number) => (
    <Tooltip placement='top' title={tooltipTitle} tooltipContent={tooltipContent}>
      <Button variant='header' className={OPTION_CLASS_NAME} disabled={isDisabled} loading={loadingKey === key} onClick={() => void run(key, limit)}>
        {label}
      </Button>
    </Tooltip>
  )

  return aiNext > 0 ? (
    <div className='d-flex flex-column gap-2'>
      <div className='paragraph-medium fw-600 zocks-text-gray-900'>
        Run AI Deep Analysis on {aiNext.toLocaleString()} {pluralize(aiNext, 'client')}
      </div>
      <div className='paragraph-small zocks-text-gray-900'>{AI_BODY}</div>
      <div className='d-flex flex-wrap gap-2'>
        {aiNext > 10 ? option('ai-10', '10 clients', 10) : null}
        {aiNext > 20 ? option('ai-20', '20 clients', 20) : null}
        {option('ai-all', `All ${aiNext.toLocaleString()} ${pluralize(aiNext, 'client')}`)}
      </div>
      <form className='d-flex align-items-center gap-2' onSubmit={handleCustomSubmit}>
        <Form.Control
          type='number'
          min={1}
          max={aiNext}
          step={1}
          inputMode='numeric'
          value={customCount}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCustomCount(event.currentTarget.value)}
          placeholder={AI_CUSTOM_PLACEHOLDER}
          aria-label={AI_CUSTOM_LABEL}
          disabled={isDisabled}
        />
        <Tooltip placement='top' title={tooltipTitle} tooltipContent={tooltipContent}>
          <Button type='submit' variant='header' className={OPTION_CLASS_NAME} disabled={isDisabled || !isCustomValid} loading={loadingKey === 'ai-custom'}>
            {AI_CUSTOM_RUN}
          </Button>
        </Tooltip>
      </form>
    </div>
  ) : null
}
